import {
  FroelingData, FroelingHistoryPoint, FroelingSetting, FroelingSettingChange, FroelingSettingsGroup, FroelingSettingsSnapshot, PelletDayStat,
} from '../version.service';
import { heatingCircuitColor, setpointColor } from './chart.utils';

/**
 * Fröling helpers shared by the Heizung section and the Analyse board: the boiler's states (Kesselzustand) and
 * their categories, the burner on / off of a point, the series of the "Hydraulik" diagnosis chart, the pellet
 * store's days left and the settings view's texts (labels, values, time windows, change-log entries, neutral hints),
 * shared by the Heizung section's settings view and the Analyse board's settings widget.
 */

/** i18n keys of the boiler states (Kesselzustand) by their numeric code (the controller's own text for the rest). */
export const BOILER_STATES: Readonly<Record<string, string>> = {
  '0': 'HEIZUNG_STATE_FAULT', '1': 'HEIZUNG_STATE_OFF', '2': 'HEIZUNG_STATE_HEATING_UP', '3': 'HEIZUNG_STATE_HEATING',
  '4': 'HEIZUNG_STATE_SLUMBER', '5': 'HEIZUNG_STATE_FIRE_OFF', '6': 'HEIZUNG_STATE_DOOR_OPEN',
  '7': 'HEIZUNG_STATE_PREPARATION', '8': 'HEIZUNG_STATE_PREHEATING', '9': 'HEIZUNG_STATE_IGNITION',
  '10': 'HEIZUNG_STATE_SHUTDOWN', '11': 'HEIZUNG_STATE_SHUTDOWN', '12': 'HEIZUNG_STATE_SHUTDOWN',
  '13': 'HEIZUNG_STATE_SHUTDOWN', '14': 'HEIZUNG_STATE_SHUTDOWN', '15': 'HEIZUNG_STATE_CLEANING',
  '16': 'HEIZUNG_STATE_WAIT_2H', '17': 'HEIZUNG_STATE_SUCTION', '18': 'HEIZUNG_STATE_MISFIRE',
  '19': 'HEIZUNG_STATE_READY',
};
/** The same states by their German name (lower case), should the Fröling report the name instead of the code. */
export const BOILER_STATE_NAMES: Readonly<Record<string, string>> = {
  'störung': '0', 'kessel aus': '1', 'anheizen': '2', 'heizen': '3', 'feuererhaltung': '4', 'feuer aus': '5',
  'tür offen': '6', 'vorbereitung': '7', 'vorwärmen': '8', 'zünden': '9', 'abstellen': '10', 'abreinigen': '15',
  '2h warten': '16', 'saugen/heizen': '17', 'fehlzündung': '18', 'betriebsbereit': '19',
};

/** Categories of the boiler states, bottom to top on the state chart's axis. */
export const STATE_CATEGORIES = ['off', 'ready', 'service', 'shutdown', 'start', 'heating', 'other', 'fault'] as const;
export type StateCategory = typeof STATE_CATEGORIES[number];
/** i18n keys of the categories. */
export const STATE_CATEGORY_KEYS: Readonly<Record<StateCategory, string>> = {
  off: 'FROELING_CAT_OFF', ready: 'FROELING_CAT_READY', service: 'FROELING_CAT_SERVICE', shutdown: 'FROELING_CAT_SHUTDOWN',
  start: 'FROELING_CAT_START', heating: 'FROELING_CAT_HEATING', other: 'FROELING_CAT_OTHER', fault: 'FROELING_CAT_FAULT',
};
/** Short i18n names of the categories (the state chart's axis labels: the board's charts share its width). */
export const STATE_CATEGORY_SHORT_KEYS: Readonly<Record<StateCategory, string>> = {
  off: 'FROELING_CAT_OFF', ready: 'FROELING_CAT_READY', service: 'FROELING_CAT_SERVICE_SHORT', shutdown: 'FROELING_CAT_SHUTDOWN_SHORT',
  start: 'FROELING_CAT_START', heating: 'FROELING_CAT_HEATING', other: 'FROELING_CAT_OTHER_SHORT', fault: 'FROELING_CAT_FAULT',
};

const CAT_CODES: Record<Exclude<StateCategory, 'other'>, number[]> = {
  // the fire burns: heating up, heating, fire maintenance, suction while heating, emergency / manual heating
  heating: [2, 3, 4, 17, 60, 61, 70, 71, 72, 73, 83, 86],
  // getting the fire going
  start: [7, 8, 9, 22, 27, 30, 34, 35, 56, 79, 80],
  // burning out: shutdown waits, remaining feed, fan run-on, stopped
  shutdown: [10, 11, 12, 13, 14, 28, 31, 32, 33, 75, 89],
  // cleaning, grate, stoker, pellet suction
  service: [15, 20, 21, 23, 24, 25, 26, 29, 36, 37, 38, 39, 40, 55, 69, 76, 78, 90],
  ready: [16, 19],
  off: [1, 5, 77],
  fault: [0, 18, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 57, 58, 62, 63, 66, 67, 68, 74, 81],
};
const CATEGORY_OF = new Map<number, StateCategory>();
for (const [cat, codes] of Object.entries(CAT_CODES)) for (const c of codes) CATEGORY_OF.set(c, cat as StateCategory);

/** The category of a state code (an unknown code: 'other'). */
export function stateCategory(code: number | null | undefined): StateCategory | null {
  if (code == null || !isFinite(code)) return null;
  return CATEGORY_OF.get(Math.round(code)) ?? 'other';
}

/** Exhaust temperature from which a point without a state code counts as burning (the pellet ledger's ON threshold). */
export const BURN_EXHAUST_C = 80;

/** The burner is on at this point: its state code says so, or (older points without one) its exhaust is hot. */
export function burning(p: Pick<FroelingHistoryPoint, 'boilerStateCode' | 'exhaustTempC'>): boolean {
  const cat = stateCategory(p.boilerStateCode);
  if (cat) return cat === 'heating';
  return (p.exhaustTempC ?? 0) >= BURN_EXHAUST_C;
}

/**
 * The share of the point's time the burner burned (0–1; null: unknown): the archived burnerOn (a minute: 0 / 1, a
 * downsampled bucket: its exact share — a bucket's most frequent state code would hide a burn shorter than half
 * the bucket), else (older points) from the state code / the exhaust.
 */
export function burnerShare(p: Pick<FroelingHistoryPoint, 'burnerOn' | 'boilerStateCode' | 'exhaustTempC'>): number | null {
  if (typeof p.burnerOn === 'number' && isFinite(p.burnerOn)) return Math.max(0, Math.min(1, p.burnerOn));
  if (p.boilerStateCode == null && !(p.exhaustTempC > 0)) return null;
  return burning(p) ? 1 : 0;
}

/** Translated text of a boiler state: the dashboard's own translation of codes 0–19, else the controller's text. */
export function boilerStateText(state: string | undefined, controllerText: string | undefined, t: (k: string) => string): string {
  if (!state && !controllerText) return '';
  const s = (state ?? '').trim();
  const key = BOILER_STATES[s] ?? BOILER_STATES[BOILER_STATE_NAMES[s.toLowerCase()] ?? ''];
  return key ? t(key) : controllerText || s;
}

// ── the Hydraulik chart ─────────────────────────────────────────────────────

/** One series of the Hydraulik chart. */
export interface HydSeries {
  /** Stable key (deep link / legend selection). */
  key: string;
  label: string;
  color: string;
  /** 'temp': °C on the left axis; 'pct': 0–100 % on the right axis. */
  axis: 'temp' | 'pct';
  /** Flattened archive field (stats, min–max bands); null: derived (the burner). */
  field: string | null;
  value: (p: FroelingHistoryPoint) => number | null;
  dash?: number[];
  /** A pump / the burner: a stepped area. */
  area?: boolean;
  /** A set-point: 0 = none (a gap). */
  soll?: boolean;
}

function num(v: unknown): number | null {
  return typeof v === 'number' && isFinite(v) ? v : null;
}

/**
 * The Hydraulik chart's series on one time axis: boiler, buffer top / bottom, each heating circuit's flow (solid)
 * and set-point (dashed), the DHW tank — and the buffer pump and DHW pump (0–100 %) and the burner (on 1 / off 0).
 * Series without a single value in `pts` (an older archive window) are left out. `compact`: the card's chart
 * (temperatures of circuit 01 only, no pumps).
 */
export function hydraulicsSeries(pts: readonly FroelingHistoryPoint[], t: (k: string, p?: any) => string, dark: boolean,
                                  hcLabel: (name: string) => string, compact = false): HydSeries[] {
  const out: HydSeries[] = [
    { key: 'boilerTempC', label: t('HEIZUNG_KESSEL'), color: '#dc3545', axis: 'temp', field: 'boilerTempC', value: p => num(p.boilerTempC) },
    { key: 'bufferTopTempC', label: t('BOARD_DS_BUFFER_TOP'), color: dark ? '#6ea8fe' : '#0d6efd', axis: 'temp', field: 'bufferTopTempC',
      value: p => num(p.bufferTopTempC) },
  ];
  if (!compact) {
    out.push({ key: 'bufferBottomTempC', label: t('BOARD_DS_BUFFER_BOTTOM'), color: dark ? '#9ec5fe' : '#6ea8fe', axis: 'temp',
      field: 'bufferBottomTempC', value: p => num(p.bufferBottomTempC), dash: [4, 3] });
  }
  const names = pts.find(p => p.heatingCircuits?.length)?.heatingCircuits.map(h => h.name) ?? [];
  const sollDashes: number[][] = [[5, 4], [2, 3], [8, 3, 2, 3], [1, 3]];
  names.slice(0, compact ? 1 : names.length).forEach((name, i) => {
    const hc = (p: FroelingHistoryPoint) => p.heatingCircuits?.find(h => h.name === name);
    const label = hcLabel(name);
    out.push({ key: `hc:${name}`, label: t('HYD_HC_IST', { name: label }), color: heatingCircuitColor(i, dark), axis: 'temp',
      field: `heatingCircuits.${name}.flowTempActualC`, value: p => num(hc(p)?.flowTempActualC) });
    out.push({ key: `hcSoll:${name}`, label: t('HYD_HC_SOLL', { name: label }), color: setpointColor(dark),
      axis: 'temp', field: `heatingCircuits.${name}.flowTempTargetC`, soll: true, dash: sollDashes[i % sollDashes.length],
      value: p => { const v = num(hc(p)?.flowTempTargetC); return v != null && v > 0 ? v : null; } });
  });
  out.push({ key: 'hotWaterTempC', label: t('HYD_DHW'), color: dark ? '#3dd5f3' : '#0aa2c0', axis: 'temp', field: 'hotWaterTempC',
    value: p => num(p.hotWaterTempC) });
  if (!compact) {
    out.push({ key: 'bufferPumpPct', label: t('HYD_BUFFER_PUMP'), color: dark ? '#8c68cd' : '#6f42c1', axis: 'pct', field: 'bufferPumpPct',
      value: p => num(p.bufferPumpPct), area: true, dash: [3, 2] });
    out.push({ key: 'dhwPumpPct', label: t('HYD_DHW_PUMP'), color: dark ? '#20c997' : '#12b886', axis: 'pct', field: 'dhwPumpPct',
      value: p => num(p.dhwPumpPct), area: true, dash: [1, 2] });
  }
  out.push({ key: 'burner', label: t('HYD_BURNER'), color: BURNER_COLOR(dark), axis: 'pct', field: 'burnerOn', area: true,
    value: p => burnerShare(p) });
  // a series the window has no value of (older points) is left out
  return out.filter(s => s.key === 'burner' || pts.some(p => s.value(p) != null));
}

/** The burner's shading colour (a box in the legend, not a line: see {@link burnerLegend}). */
export const BURNER_COLOR = (dark: boolean): string => dark ? '#ff8787' : '#e03131';

/** Dataset options that make the legend show a filled box (the area's fill) instead of a line swatch. */
export function burnerLegend(color: string, dark: boolean): Record<string, unknown> {
  return { legendBox: true, legendFill: areaFill(color, dark, true, dark ? 0.45 : 0.35) };
}

/** Fill colour of a pump / burner area (alpha: an explicit opacity). */
export function areaFill(color: string, dark: boolean, burner: boolean, alpha?: number): string {
  const a = alpha ?? (burner ? (dark ? 0.16 : 0.1) : (dark ? 0.14 : 0.08));
  const hex = color.replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16), g = parseInt(hex.slice(2, 4), 16), b = parseInt(hex.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

// ── pellet store ─────────────────────────────────────────────────────────────

/** Mean kg per day over the last `days` complete days of the daily list (today left out); null with too few days. */
export function recentDailyKg(stats: readonly PelletDayStat[], days = 14): number | null {
  const full = stats.filter(s => !s.inProgress).slice(-days);
  if (full.length < 3) return null;
  return full.reduce((a, s) => a + (s.consumptionKg ?? 0), 0) / full.length;
}

/** Days the store lasts at `kgPerDay` (null: unknown, or hardly any consumption). */
export function storeDaysLeft(storeT: number | null | undefined, kgPerDay: number | null): number | null {
  if (storeT == null || !isFinite(storeT) || kgPerDay == null || kgPerDay < 0.5) return null;
  return Math.floor(storeT * 1000 / kgPerDay);
}

// ── settings ─────────────────────────────────────────────────────────────────

/** A neutral note on a setting the heating analysis flagged for the installer (i18n key + params), or null. */
export interface SettingHint { key: string; params?: Record<string, unknown>; }

/** The note for one setting of a group, given the live reading (null: none). */
export function settingHint(group: FroelingSettingsGroup, s: FroelingSetting, live: FroelingData | null): SettingHint | null {
  const v = parseFloat(s.value);
  switch (`${group.type}/${s.key}`) {
    case 'BOILER/boilerSetTemp':
      return v >= 75 ? { key: 'FS_HINT_BOILER_SET', params: { v } } : null;
    case 'DHW/setDhwTemp': {
      const reload = parseFloat(group.settings.find(x => x.key === 'reloadBelowTemp')?.value ?? '');
      return v <= 40 ? { key: 'FS_HINT_DHW_SET', params: { v, reload: isFinite(reload) ? reload : '–' } } : null;
    }
    case 'BUFFER_TANK/circuitReleaseTempLimit':
      return v <= 35 ? { key: 'FS_HINT_RELEASE', params: { v } } : null;
    case 'CIRCULATION_PUMP/pumpOffAboveTemp':
      return live?.circulationReturnNoSensor || (live?.circulationReturnTempC === 0)
        ? { key: 'FS_HINT_CIRC_SENSOR', params: { v } } : null;
  }
  return null;
}

/** Group icon by component type. */
export function groupIcon(type: string): string {
  switch (type) {
    case 'BOILER': return '🔥';
    case 'CIRCUIT': return '🌡️';
    case 'DHW': return '🚿';
    case 'BUFFER_TANK': return '🛢️';
    case 'SOLAR': return '☀️';
    case 'FEED_SYSTEM': return '🪵';
    case 'CIRCULATION_PUMP': return '🔄';
    default: return '⚙️';
  }
}

/** "Mo–So 06:00–23:00" when every day has the same windows, else one line per run of equal days. */
export function windowsSummary(days: { weekDay: string; phases: string[] }[], dayName: (wd: string) => string): string[] {
  const out: string[] = [];
  let i = 0;
  while (i < days.length) {
    let j = i;
    const ph = days[i].phases.join(', ');
    while (j + 1 < days.length && days[j + 1].phases.join(', ') === ph) j++;
    const span = i === j ? dayName(days[i].weekDay) : `${dayName(days[i].weekDay)}–${dayName(days[j].weekDay)}`;
    out.push(`${span} ${ph ? ph.replace(/-/g, '–') : '—'}`);
    i = j + 1;
  }
  return out;
}

/** i18n lookup (TranslateService.instant). */
type Tr = (key: string, params?: Record<string, unknown>) => string;

/** A setting's short label in the UI language (FS_<type>_<key> where it differs by component type, else FS_<key>),
 *  else the controller's. */
export function settingLabel(g: Pick<FroelingSettingsGroup, 'type'>, s: Pick<FroelingSetting, 'key' | 'label'>, t: Tr): string {
  for (const k of [`FS_${g.type}_${s.key}`, `FS_${s.key}`]) {
    const v = t(k);
    if (v !== k) return v;
  }
  return s.label || s.key;
}

/** A controller value ("58.0") as a number in the UI locale ("58", "21,5"); anything that is no plain number as it is. */
export function fmtSettingNumber(raw: string, locale: string): string {
  const r = (raw ?? '').trim();
  return /^-?\d+(\.\d+)?$/.test(r) ? parseFloat(r).toLocaleString(locale, { maximumFractionDigits: 1 }) : raw;
}

/** A setting's value: the code's text, else a number in the UI locale with its unit. */
export function settingValue(s: FroelingSetting, locale: string): string {
  if (s.text) return s.text;
  const v = fmtSettingNumber(s.value, locale);
  return s.unit ? `${v} ${s.unit}` : v;
}

/** A component's name in the UI language ("Hot-water tank 01" for the controller's "Boiler 01", "Heating Circuit 01"
 *  for "Heizkreis 01"), by its type; `fallback` without a group. */
export function settingGroupLabel(g: Pick<FroelingSettingsGroup, 'type' | 'name'> | undefined, t: Tr, fallback = ''): string {
  if (!g) return fallback;
  if (g.type === 'CIRCUIT') {
    const m = g.name.match(/(\d+)/);
    return m ? `${t('HEIZKREIS')} ${m[1]}` : g.name;
  }
  const key = 'FS_GROUP_' + g.type;
  const label = t(key);
  if (label === key) return g.name;
  const n = g.name.match(/\d+/);
  return n ? `${label} ${n[0]}` : label;
}

/** Every neutral hint of the snapshot with its group (the hint box), given the live reading. */
export function settingHints(fs: FroelingSettingsSnapshot | null, live: FroelingData | null): { group: FroelingSettingsGroup; hint: SettingHint }[] {
  const out: { group: FroelingSettingsGroup; hint: SettingHint }[] = [];
  for (const g of fs?.components ?? []) {
    for (const s of g.settings) {
      const h = settingHint(g, s, live);
      if (h) out.push({ group: g, hint: h });
    }
  }
  return out;
}

const DAY_CODES: Record<string, string> = {
  MONDAY: 'MO', TUESDAY: 'TU', WEDNESDAY: 'WE', THURSDAY: 'TH', FRIDAY: 'FR', SATURDAY: 'SA', SUNDAY: 'SU',
};

/** "Mo" for "MONDAY" (or the backend's "MO"). */
function dayName(wd: string, t: Tr): string {
  return t('FS_DAY_' + (DAY_CODES[wd] ?? wd.slice(0, 2).toUpperCase()));
}

/** A component's time windows, days with equal windows merged ("Mo–So 06:00–23:00"). */
export function settingWindowLines(g: Pick<FroelingSettingsGroup, 'timeWindows'>, t: Tr): string[] {
  return windowsSummary(g.timeWindows ?? [], wd => dayName(wd, t));
}

/** The backend's comparable week text ("MO 06:00-23:00 · TU 06:00-23:00,17:00-20:00 · WE -") as merged, translated
 *  lines ("Mo–So 06:00–23:00"); a text it cannot read as it is. */
export function windowsText(text: string, t: Tr): string {
  const days = text.split(' · ').map(part => {
    const m = part.trim().match(/^(\S+)\s+(.*)$/);
    return m ? { weekDay: m[1], phases: m[2] === '-' ? [] : m[2].split(',') } : null;
  });
  if (!days.length || days.some(d => d == null)) return text;
  return windowsSummary(days as { weekDay: string; phases: string[] }[], wd => dayName(wd, t)).join(', ');
}

/** One change-log entry as shown: the component and setting in the UI language, the old and the new value (the
 *  code's text where known, else the number in the UI locale as in the settings list; the unit after the new one). */
export interface SettingChangeParts { component: string; setting: string; from: string; to: string; }

export function settingChangeParts(c: FroelingSettingChange, fs: FroelingSettingsSnapshot | null, t: Tr, locale: string): SettingChangeParts {
  const g = fs?.components.find(x => x.componentId === c.componentId);
  const component = settingGroupLabel(g, t, c.component);
  if (c.key === 'timeWindows') {
    return { component, setting: t('FS_TIME_WINDOWS'), from: windowsText(c.oldValue, t), to: windowsText(c.newValue, t) };
  }
  const s = g?.settings.find(x => x.key === c.key);
  return {
    component,
    setting: g && s ? settingLabel(g, s, t) : settingLabel({ type: g?.type ?? '' }, c, t),
    from: c.oldText || fmtSettingNumber(c.oldValue, locale),
    to: c.newText ? c.newText : fmtSettingNumber(c.newValue, locale) + (c.unit ? ' ' + c.unit : ''),
  };
}
